/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2017, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'react',
	'react-dom',
	'@ba/ba-ui-carbon-toolkit',
	'underscore',
	'../../../datatray/ExtensionBaseView',
	'../../../dataSources/nls/StringResources'
], (React, ReactDOM, toolkit, _, BaseView, StringResources) => {

	class ContextualGrid extends BaseView {

		constructor(options) {
			super(options);
			this.dashboard = options.dashboard;
			this.logger = this.dashboard.getGlassCoreSvc('.Logger');
		}

		render(content, container) {
			if( this.content !== content ||  this.containerNode !== container) {
				this.setViewContext(content, container);
			}

			BaseView.clearContainer(this.containerNode);

			// class name is needed for using style from ba-ui-toolkit
			this.containerNode.classList.add('ba-theme-waca');
			this.containerNode.classList.add('behindVisView');
			// get the view option from vis grid model in widget model
			const doAggregate = content.getPropertyValue('datagrid.viewOption') === 'projectedAggregated';
			this._setLoading();
			return this.content.getFeature('DataBehindTheVis').getData(doAggregate).then(results => {
				this._removeLoading();
				if(this._isResultValid(results)){
					this._renderReactGrid(results);
				} else {
					this._noData();
				}
			}).catch((error) => {
				const knownErrors = ['dwErrorStaleRequest'];
				const errorMessage = (error && (error.message || error.msg)) || 'dwErrorRenderingContextualGrid';
				if (knownErrors.indexOf(errorMessage) === -1){
					//TODO: we could introduce a bad state view here
					this._removeLoading();
					this.logger.error(error);
				} else {
					this.logger.warn(error);
				}
			});
		}

		setViewContext(content, container){
			this._deregisterEvents();
			this.content = content;

			this.containerNode = container;

			this._registerEvents();
		}

		_noData() {
			const emptyStateProps = {
				type: 'default',
				size: 'large'
			};
			const emptyState = React.createElement(toolkit.EmptyState, emptyStateProps);

			const text = React.createElement('div', { className: 'emptyStateTitle' }, StringResources.get('DATA_TRAY_no_data_text'));

			const content = React.createElement('div', { className: 'noDataContent' }, emptyState, text);
			ReactDOM.render(content, this.containerNode);
		}

		remove() {
			ContextualGrid.inherited('remove', this, arguments);
			this._deregisterEvents();
			this._unmountReactComponents();
		}

		_unmountReactComponents() {
			if (this.containerNode) {
				ReactDOM.unmountComponentAtNode(this.containerNode);
			}
		}

		_registerEvents() {
			if (this.content) {
				this._renderHandler = this.render.bind(this, this.content, this.containerNode);
				this.content.on('change:property:datagrid.viewOption', this._renderHandler, this);
			}
		}

		_deregisterEvents() {
			if (this.content) {
				this.content.off('change:property:datagrid.viewOption', this._renderHandler, this);
			}
		}

		_hasTopNOnFact(content) {
			//Return TRUE if some item in this visualization has a nonContextual TopN (a TopN on a fact)
			const visualization = content && this.content.getFeature('Visualization');
			const mappedDataItemList = visualization.getSlots().getDataItemList();

			const topNItem = mappedDataItemList.find((di) => di.getType()==='fact' && di.getTopBottom());

			return !!topNItem;
		}

		_isMenuOptionValid(content, menuOptionId) {
			//We can show unAggregated data UNLESS there's a nonContextual topN in this visualization. 
			//Using id's as defined in ContextualGridActionMenu.json.
			if (menuOptionId==='com.ibm.bi.dashboard.gridShowUnaggregated' ||
				menuOptionId==='com.ibm.bi.dashboard.gridRevertToOrginal') {
				return !this._hasTopNOnFact(content);
			}
			return true;
		}

		_renderReactGrid(results) {
			var props = {
				numColumns: results.headers.length,
				numRows: results.cells.length,
				fixedRows: 1,
				useRowOptionsColumn: true,
				rowOptionsColumnText: StringResources.get('contextualGridMenuLabel'),
				dataCellRenderer: this._cellRenderer.bind(this, results),
				fixedCellVerticalBorder: false,
				fixedCellHorizontalBorder: false,
				fixedRowCellRenderer: (_, colIndex, gridCellProps) => {
					var headerData = results.headers[colIndex];
					gridCellProps.style['border'] = 'none';
					let reactEle =  React.createElement(
						toolkit.GridHeaderCell,
						gridCellProps,
						React.createElement(
							'div',
							{
								style: BaseView.CELL.style.left
							},
							headerData
						)
					);
					reactEle.displayName = 'RowCell';
					return reactEle;
				},
				onFixedCornerCellMenu: (clientX, clientY) => {
					this.dashboard.showContextMenu({
						position: {
							pageX: clientX,
							pageY: clientY
						},
						menuId: 'com.ibm.bi.dashboard.contextualGridMenu',
						activeObject: {
							content: this.content,
							export: this._export.bind(this, results),
							isMenuOptionValid: this._isMenuOptionValid.bind(this, this.content)
						}
					});
				}
			};
			var contextualGrid = React.createElement(toolkit.Grid, props);
			ReactDOM.render(contextualGrid, this.containerNode);
		}

		_export(results) {
			this.dashboard.getFeature('CsvExport').export(results.title, [results.headers], results.cells);
		}

		_cellRenderer(results, rowIndex, colIndex, gridCellProps) {

			const dataCellObj = results.cells[rowIndex][colIndex];

			let style = BaseView.CELL.style.left;

			if (this._isCellValueMeasure(dataCellObj)) {
				style = BaseView.CELL.style.right;
			}

			return this._getCellComponent(dataCellObj, gridCellProps, style);
		}


		_isCellValueMeasure(dataCell) {
			return dataCell && _.isUndefined(dataCell.label);
		}

		_getCellComponent(dataCell, gridCellProps, style){
			if(style){
				style['white-space'] = 'nowrap';/*Deal with very long column value*/
			}
			gridCellProps.style['border-right'] = 'none';
			const dataCellValue = dataCell.toString();
			let reactEle = <toolkit.GridCell
				{...gridCellProps}
			>
				<div
					style={style}
					title={dataCellValue}
				>
					{dataCellValue}
				</div>
			</toolkit.GridCell>;
			reactEle.displayName = 'Cell';
			return reactEle;
		}

		_isResultValid(result){
			return result.cells.length && result.headers.length;
		}
	}

	return ContextualGrid;
});